/* Utils */
import {extractQueryParamPairStrings} from '../../common/url/urlUtils';

/* Constants */
import {PAGINATION_PARAM_NAMES} from '../../common/pagination/paginationConstants';
import {SEO_PARAGRAPH_ELEMENT_ID_AT_PAGE_TOP} from './seoConstants';

const SEO_ROBOTS_META_CONTENT_DELIMITER = ', ';

function removeSeoContentAtPageTop(){
  const seoRootElement = document.getElementById(SEO_PARAGRAPH_ELEMENT_ID_AT_PAGE_TOP);

  if (seoRootElement) {
    seoRootElement.remove();
  }
}

function generateRobotsValueWithHeaders(
  actualIndexValue,
  actualFollowValue,
  indexHeader,
  followHeader,
) {
  if (indexHeader && followHeader) {
    return `${indexHeader}, ${followHeader}`
  }

  if (indexHeader) {
    return `${indexHeader}, ${actualFollowValue}`
  }

  if (followHeader) {
    return `${actualIndexValue}, ${followHeader}`
  }

  return `${actualIndexValue}, ${actualFollowValue}`
}

function generateSeoRobotsMetaContent(index, follow) {
  let result = null;

  if (index || follow) {
    const partials = [];

    if (index) {
      partials.push(index);
    }

    if (follow) {
      partials.push(follow);
    }

    result = partials.join(SEO_ROBOTS_META_CONTENT_DELIMITER);
  }

  return result;
}

function generateSearchKeywordArray(rawSearchKeywords = []) {
  if (rawSearchKeywords) {
    return rawSearchKeywords.map(
      rawSearchKeyword => ({
        title: rawSearchKeyword.title,
        link: rawSearchKeyword.link,
        text: rawSearchKeyword.keyword,
      })
    );
  }

  return null;
}

function generateSeoInfo(rawSeoInformation) {
  let result;

  if (rawSeoInformation) {
    result = {
      appLink: rawSeoInformation.appLink,
      canonicalUrl: rawSeoInformation.canonicalUrl,
      description: rawSeoInformation.description,
      follow: rawSeoInformation.follow,
      h1: rawSeoInformation.h1,
      h2: rawSeoInformation.h2,
      index: rawSeoInformation.index,
      isBrandIndexed: rawSeoInformation.isBrandIndexed,
      searchKeywordArray: generateSearchKeywordArray(
        rawSeoInformation.keywords,
      ),
      next: rawSeoInformation.next,
      prev: rawSeoInformation.prev,
      seoParagraph: rawSeoInformation.seoParagraph,
      showSeoInformation: rawSeoInformation.showSeoInformation,
      title: rawSeoInformation.title,
      utagData: rawSeoInformation.utagData,
      questions: rawSeoInformation.questions,
      breadcrumbArray: rawSeoInformation.breadcrumbs,
      isActiveAddToCart: rawSeoInformation.isActiveAddToCart
    };
  } else {
    result = null;
  }

  return result;
}

function setGlobalUtagDataIfOnClient(utagData) {
  if (process.env.BROWSER) {
    window.utagData = utagData;
    window.utagData.page_url = window.location.href;
    window.utagData.page_referring_url = '';
    window.utagData.page_query_string = extractQueryParamPairStrings(
      window.location.search,
    );
    window.utagData.project = 'pwa';
  }
}

function setGlobalDataLayerIfOnClient(utagData) {
  if (process.env.BROWSER && window.dataLayer) {
    Object.assign(window.dataLayer, utagData);

    window.dataLayer.page_url = window.location.href ? window.location.href : '';
    window.dataLayer.page_referring_url = '';
    window.dataLayer.page_query_string = extractQueryParamPairStrings(
      window.location.search
    );
  }
}

function isSeoParagraphViewEnabledForListingPages(queryParams, isLoadMoreModeEnabled) {
  const paginationParam = queryParams[PAGINATION_PARAM_NAMES.PAGE];

  return !paginationParam ||
    (paginationParam && paginationParam === '1') ||
    (process.env.BROWSER && isLoadMoreModeEnabled);
}

function generateStructuredBreadCrumbDataProps() {
  return {
    listItemProps: {
      itemScope: true,
      itemType: 'https://data-vocabulary.org/Breadcrumb',
    },
    linkProps: {
      itemProp: 'url',
    },
    linkContentProps: {
      itemProp: 'title',
    },
  };
}

function generateSeoBreadCrumbConfigs(seoBreadCrumbArray) {
  return [{
    id: '/',
    labelText: 'Anasayfa',
    url: '/',
    isRoot: true,
    ...generateStructuredBreadCrumbDataProps(),
  }].concat(seoBreadCrumbArray.map(
    breadCrumb => {
      return {
        id: breadCrumb.url,
        labelText: breadCrumb.name,
        url: breadCrumb.url.startsWith('/') ? breadCrumb.url : '/' + breadCrumb.url,
        ...generateStructuredBreadCrumbDataProps(),
      };
    }
  ));
}

const shouldRenderSeoContent = ({page}) => {
  return process.env.BROWSER ? true : page === 1
}

export {
  removeSeoContentAtPageTop,
  generateSeoRobotsMetaContent,
  generateSeoInfo,
  setGlobalUtagDataIfOnClient,
  setGlobalDataLayerIfOnClient,
  isSeoParagraphViewEnabledForListingPages,
  generateStructuredBreadCrumbDataProps,
  generateSeoBreadCrumbConfigs,
  shouldRenderSeoContent,
  generateRobotsValueWithHeaders,
};
